import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '@proptr/api-client/react';
import { LoginPage } from './pages/LoginPage.js';
import { OtpPage } from './pages/OtpPage.js';
import { AcceptInvitePage } from './pages/AcceptInvitePage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { AgenciesPage } from './pages/AgenciesPage.js';
import { AgencyDetailPage } from './pages/AgencyDetailPage.js';
import { ProptreeSettingsView } from './pages/agency/ProptreeSettingsView.js';
import { AgencySettingsView } from './pages/agency/AgencySettingsView.js';
import { DatasourcesView } from './pages/agency/DatasourcesView.js';
import { AgencyAgentsView } from './pages/agency/AgencyAgentsView.js';
import { AgencyUsersView } from './pages/agency/AgencyUsersView.js';
import { CronHandlerPage } from './pages/CronHandlerPage.js';
import { ScrapersPage } from './pages/ScrapersPage.js';
import { ScraperDetailPage } from './pages/ScraperDetailPage.js';
import { UsersPage } from './pages/UsersPage.js';
import { PartnersPage } from './pages/PartnersPage.js';
import { PartnerDetailPage } from './pages/PartnerDetailPage.js';

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
      <Route path="/verify" element={<PublicOnly><OtpPage /></PublicOnly>} />
      {/* Not PublicOnly: an invite link opened in a browser where another
          admin is signed in should still reach the invitee's form. */}
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/" element={<RequireAdmin><DashboardPage /></RequireAdmin>} />
      <Route
        path="/agencies"
        element={<RequireAdmin><AgenciesPage /></RequireAdmin>}
      />
      <Route
        path="/agency/:enterpriseId"
        element={<RequireAdmin><AgencyDetailPage /></RequireAdmin>}
      >
        <Route index element={<Navigate to="proptree" replace />} />
        <Route path="proptree" element={<ProptreeSettingsView />} />
        <Route path="settings" element={<AgencySettingsView />} />
        <Route path="datasources" element={<DatasourcesView />} />
        <Route path="agents" element={<AgencyAgentsView />} />
        <Route path="users" element={<AgencyUsersView />} />
      </Route>
      <Route
        path="/users"
        element={<RequireAdmin><UsersPage /></RequireAdmin>}
      />
      <Route
        path="/partners"
        element={<RequireAdmin><PartnersPage /></RequireAdmin>}
      />
      <Route
        path="/partners/:partnerId"
        element={<RequireAdmin><PartnerDetailPage /></RequireAdmin>}
      />
      <Route
        path="/scrapers"
        element={<RequireAdmin><ScrapersPage /></RequireAdmin>}
      />
      <Route
        path="/scrapers/:scraperId"
        element={<RequireAdmin><ScraperDetailPage /></RequireAdmin>}
      />
      <Route
        path="/cron"
        element={<RequireAdmin><CronHandlerPage /></RequireAdmin>}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function RequireAdmin({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (user.role !== 'admin') {
    return (
      <main className="grid min-h-screen place-items-center px-6 text-center">
        <div className="space-y-3">
          <div className="eyebrow">Access denied</div>
          <h1 className="font-display text-[32px] font-medium leading-none tracking-[-0.02em]">
            Not authorized
          </h1>
          <p className="font-mono text-[11px] text-muted-foreground">
            You are signed in as <code className="text-foreground">{user.role}</code>.
            This portal requires the <code className="text-foreground">admin</code> role.
          </p>
        </div>
      </main>
    );
  }
  return <>{children}</>;
}

function PublicOnly({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user && user.role === 'admin') return <Navigate to="/" replace />;
  return <>{children}</>;
}
